import { useEffect, useState } from "react"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { CalendarEvent } from "@/pages/Calendario"
import { format } from "date-fns"
import { Calendar as CalendarIcon, Clock, MapPin, Users, Bell, Tag } from "lucide-react"

interface EventDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSave: (event: Partial<CalendarEvent>) => Promise<void>
  event?: CalendarEvent | null
  defaultStart?: Date
  defaultEnd?: Date
}

export function EventDialog({ open, onOpenChange, onSave, event, defaultStart, defaultEnd }: EventDialogProps) {
  const [title, setTitle] = useState("")
  const [description, setDescription] = useState("")
  const [startDate, setStartDate] = useState("")
  const [startTime, setStartTime] = useState("")
  const [endDate, setEndDate] = useState("")
  const [endTime, setEndTime] = useState("")
  const [allDay, setAllDay] = useState(false)
  const [eventType, setEventType] = useState<CalendarEvent["event_type"]>("task")
  const [location, setLocation] = useState("")
  const [status, setStatus] = useState<CalendarEvent["status"]>("pending")
  const [reminderMinutes, setReminderMinutes] = useState("15")
  const [color, setColor] = useState("")
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (event) {
      // Editar evento existente
      setTitle(event.title || "")
      setDescription(event.description || "")
      setStartDate(format(event.start, "yyyy-MM-dd"))
      setStartTime(format(event.start, "HH:mm"))
      setEndDate(format(event.end, "yyyy-MM-dd"))
      setEndTime(format(event.end, "HH:mm"))
      setAllDay(event.allDay || false)
      setEventType(event.event_type)
      setLocation(event.location || "")
      setStatus(event.status)
      setReminderMinutes(String(event.reminder_minutes || 15))
      setColor(event.color || "")
    } else {
      // Novo evento
      const start = defaultStart || new Date()
      const end = defaultEnd || new Date(start.getTime() + 60 * 60 * 1000)
      
      setTitle("")
      setDescription("")
      setStartDate(format(start, "yyyy-MM-dd"))
      setStartTime(format(start, "HH:mm"))
      setEndDate(format(end, "yyyy-MM-dd"))
      setEndTime(format(end, "HH:mm"))
      setAllDay(false)
      setEventType("task")
      setLocation("")
      setStatus("pending")
      setReminderMinutes("15")
      setColor("")
    }
  }, [event, defaultStart, defaultEnd, open])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)

    try {
      const start = allDay
        ? new Date(`${startDate}T00:00:00`)
        : new Date(`${startDate}T${startTime}:00`)
      
      const end = allDay
        ? new Date(`${endDate}T23:59:59`)
        : new Date(`${endDate}T${endTime}:00`)

      const eventData: Partial<CalendarEvent> = {
        title,
        description,
        start,
        end,
        allDay,
        event_type: eventType,
        location,
        status,
        reminder_minutes: parseInt(reminderMinutes),
        color: color || undefined,
      }

      await onSave(eventData)
    } finally {
      setSaving(false)
    }
  }

  const eventTypeLabels = {
    task: "Tarefa",
    meeting: "Reunião",
    vistoria: "Vistoria",
    instalacao: "Instalação",
    other: "Outro",
  }

  const statusLabels = {
    pending: "Pendente",
    confirmed: "Confirmado",
    cancelled: "Cancelado",
    completed: "Concluído",
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {event ? "Editar Evento" : "Novo Evento"}
            </DialogTitle>
            <DialogDescription>
              {event ? "Atualize as informações do evento" : "Preencha os detalhes do novo evento"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Título */}
            <div className="space-y-2">
              <Label htmlFor="title">Título *</Label>
              <Input
                id="title"
                placeholder="Ex: Reunião com cliente"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </div>

            {/* Descrição */}
            <div className="space-y-2">
              <Label htmlFor="description">Descrição</Label>
              <Textarea
                id="description"
                placeholder="Adicione detalhes sobre o evento..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />
            </div>

            {/* Tipo e Status */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="eventType" className="flex items-center gap-2">
                  <Tag className="h-4 w-4" />
                  Tipo
                </Label>
                <Select value={eventType} onValueChange={(value: CalendarEvent["event_type"]) => setEventType(value)}>
                  <SelectTrigger id="eventType">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(eventTypeLabels).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select value={status} onValueChange={(value: CalendarEvent["status"]) => setStatus(value)}>
                  <SelectTrigger id="status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(statusLabels).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Dia inteiro */}
            <div className="flex items-center space-x-2">
              <Switch
                id="allDay"
                checked={allDay}
                onCheckedChange={setAllDay}
              />
              <Label htmlFor="allDay" className="cursor-pointer">
                Evento de dia inteiro
              </Label>
            </div>

            {/* Data e Hora de Início */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDate" className="flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4" />
                  Data de Início *
                </Label>
                <Input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                />
              </div>
              {!allDay && (
                <div className="space-y-2">
                  <Label htmlFor="startTime" className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Hora de Início *
                  </Label>
                  <Input
                    id="startTime"
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    required
                  />
                </div>
              )}
            </div>

            {/* Data e Hora de Fim */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="endDate">Data de Término *</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  required
                />
              </div>
              {!allDay && (
                <div className="space-y-2">
                  <Label htmlFor="endTime">Hora de Término *</Label>
                  <Input
                    id="endTime"
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    required
                  />
                </div>
              )}
            </div>

            {/* Local */}
            <div className="space-y-2">
              <Label htmlFor="location" className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Local
              </Label>
              <Input
                id="location"
                placeholder="Ex: Sala de reuniões, Endereço do cliente..."
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              />
            </div>

            {/* Lembrete */}
            <div className="space-y-2">
              <Label htmlFor="reminder" className="flex items-center gap-2">
                <Bell className="h-4 w-4" />
                Lembrete (minutos antes)
              </Label>
              <Select value={reminderMinutes} onValueChange={setReminderMinutes}>
                <SelectTrigger id="reminder">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Sem lembrete</SelectItem>
                  <SelectItem value="5">5 minutos</SelectItem>
                  <SelectItem value="15">15 minutos</SelectItem>
                  <SelectItem value="30">30 minutos</SelectItem>
                  <SelectItem value="60">1 hora</SelectItem>
                  <SelectItem value="120">2 horas</SelectItem>
                  <SelectItem value="1440">1 dia</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Cor personalizada */}
            <div className="space-y-2">
              <Label htmlFor="color">Cor personalizada (opcional)</Label>
              <div className="flex gap-2">
                <Input
                  id="color"
                  type="color"
                  value={color || "#3b82f6"}
                  onChange={(e) => setColor(e.target.value)}
                  className="w-20 h-10"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setColor("")}
                  disabled={!color}
                >
                  Usar cor padrão
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Salvando..." : event ? "Atualizar" : "Criar Evento"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}



